import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/sections";

// Editorial photo treatment for press / celebrity photography
const EDITORIAL_FILTER = {
  filter: "grayscale(0.15) contrast(1.06) brightness(0.95) saturate(0.92) sepia(0.06)",
};

const FRAME_DECOR = (
  <>
    <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold/30" />
    <div className="pointer-events-none absolute top-3 left-3 h-5 w-5 border-t border-l border-gold/60" />
    <div className="pointer-events-none absolute top-3 right-3 h-5 w-5 border-t border-r border-gold/60" />
    <div className="pointer-events-none absolute bottom-3 left-3 h-5 w-5 border-b border-l border-gold/60" />
    <div className="pointer-events-none absolute bottom-3 right-3 h-5 w-5 border-b border-r border-gold/60" />
  </>
);

const PressFrame = ({ src, alt, name, role, aspect = "aspect-[3/4]", delay = 0 }) => (
  <Reveal delay={delay} className="w-full">
    <div className={`relative ${aspect} w-full overflow-hidden`}>
      {/* Coal backdrop in case image doesn't fully fill */}
      <div className="absolute inset-0 bg-coal" />
      <img
        src={src}
        alt={alt}
        crossOrigin="anonymous"
        className="absolute inset-0 h-full w-full object-contain object-center transition-transform duration-[1400ms] ease-luxe hover:scale-[1.03]"
        style={EDITORIAL_FILTER}
      />
      {/* Bottom gradient for caption legibility */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, rgba(17,17,17,0.5) 55%, rgba(17,17,17,0.95) 100%)",
        }}
      />
      {FRAME_DECOR}
      <div className="absolute bottom-5 left-5 right-5 z-10">
        <div className="num-overline text-gold/85">{role}</div>
        <div className="mt-1 font-heading italic text-ivory text-xl md:text-2xl tracking-tight">
          {name}
        </div>
      </div>
    </div>
  </Reveal>
);

export default function Celebrity() {
  return (
    <section
      id="celebrity"
      data-testid="section-celebrity"
      className="deck-section relative bg-ink"
    >
      <div className="mx-auto w-full max-w-[1600px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <div className="num-overline">07 · In Good Company</div>
            <h2 className="mt-8 font-heading text-ivory text-4xl md:text-5xl lg:text-[60px] leading-[1.05] tracking-tightest">
              A gift worthy of
              <br />
              India's <span className="italic text-gold">most celebrated.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="font-body text-beige/75 text-[15px] leading-[1.85] max-w-md">
              From Bollywood icons to the Prime Minister of India — Aarya24KT
              has become the chosen gifting house for moments that matter, and
              for the people who define a generation.
            </p>
          </Reveal>
        </div>

        {/* Asymmetric editorial gallery */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Malaika — larger, primary */}
          <div className="lg:col-span-7">
            <PressFrame
              src={IMAGES.celebMalaika}
              alt="Malaika Arora with Aarya24KT 24KT gold foil artworks"
              name="Malaika Arora"
              role="Bollywood · Brand Friend"
              aspect="aspect-[4/5]"
              delay={0.1}
            />
          </div>
          {/* PM Modi — secondary */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <PressFrame
              src={IMAGES.celebPM}
              alt="Hon'ble Prime Minister Shri Narendra Modi receiving an Aarya24KT gift"
              name="Shri Narendra Modi"
              role="Hon'ble Prime Minister of India"
              aspect="aspect-[4/5]"
              delay={0.25}
            />
            <Reveal delay={0.4}>
              <div className="border border-gold/25 bg-coal/40 backdrop-blur-sm p-6 md:p-7">
                <div className="num-overline text-gold/85">The Aarya24KT Standard</div>
                <p className="mt-3 font-heading italic text-ivory text-xl md:text-2xl leading-snug">
                  "Gifted to be remembered."
                </p>
                <p className="mt-4 font-body text-[13px] leading-[1.7] text-beige/70">
                  Presented across film premieres, state functions, festive
                  occasions and luxury weddings — a quiet hallmark of arrival.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
